import { routePaths } from "@/App";
import { useToast } from "@/components/ui/use-toast";
import { webSocketClient } from "@/libs/api/services/WebSocketService";
import {
  addCustomEventListener,
  EVENTS,
  removeCustomEventListener,
} from "@/utils/events";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { authService } from "../services/AccountService";
import { LogoutReasons } from "../types";

const UserContext = createContext<
  | {
      accessToken: string | null;
      isLoggedIn: boolean;
      isInitialized: boolean;
      logout: () => void;
      handleKeycloakRedirect: () => void;
      login: () => void;
    }
  | undefined
>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState({
    accessToken: authService.rawAccessToken || null,
    isLoggedIn: !!authService.rawAccessToken,
    isInitialized: false,
  });

  const { toast } = useToast();

  const dispatchState = useCallback((accessToken: string) => {
    setState({
      accessToken,
      isLoggedIn: true,
      isInitialized: true,
    });

    webSocketClient.initializeSocket("chats");
    webSocketClient.initializeSocket("drivers");
  }, []);

  const handleAuthFailure = useCallback(() => {
    setState((prevState) => ({
      accessToken: null,
      isLoggedIn: false,
      isInitialized: prevState.isInitialized,
    }));
  }, []);

  const logout = useCallback(() => {
    setState({
      accessToken: null,
      isLoggedIn: false,
      isInitialized: true,
    });
    authService.logout();
  }, []);

  const login = useCallback(() => {
    authService.redirectToKeycloak(dispatchState);
  }, [dispatchState]);

  const handleKeycloakRedirect = useCallback(() => {
    authService.handleKeycloakRedirect(dispatchState);
  }, [dispatchState]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await authService.initialize(dispatchState);
      } catch (error) {
        console.error("Auth initialization failed:", error);
      } finally {
        // Always mark as initialized, even if no tokens exist
        setState((prevState) => ({
          ...prevState,
          isInitialized: true,
        }));
      }
    };

    const onTokenUpdate = (event: CustomEvent) => {
      dispatchState(event.detail);
    };

    const onLogout = (event: CustomEvent<LogoutReasons>) => {
      handleAuthFailure();

      if (event.detail === LogoutReasons.Manual) {
        toast({
          title: "Erfolgreich abgemeldet",
          variant: "success",
        });
      }
    };

    addCustomEventListener(EVENTS.ACCOUNT_TOKEN_UPDATE, onTokenUpdate);
    addCustomEventListener(EVENTS.ACCOUNT_LOGOUT, onLogout);

    initializeAuth();

    return () => {
      removeCustomEventListener(EVENTS.ACCOUNT_TOKEN_UPDATE, onTokenUpdate);
      removeCustomEventListener(EVENTS.ACCOUNT_LOGOUT, onLogout);
    };
  }, [dispatchState, handleAuthFailure, toast]);

  const values = useMemo(
    () => ({
      accessToken: state.accessToken,
      isLoggedIn: state.isLoggedIn,
      isInitialized: state.isInitialized,
      handleKeycloakRedirect,
      login,
      logout,
    }),
    [handleKeycloakRedirect, login, logout, state],
  );

  return <UserContext.Provider value={values}>{children}</UserContext.Provider>;
}

export function useUser() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }

  useEffect(() => {
    if (
      context.isInitialized &&
      !context.isLoggedIn &&
      location.pathname !== routePaths.login
    ) {
      navigate(routePaths.login, {
        replace: true,
      });
    }
  }, [context.isInitialized, context.isLoggedIn, navigate]);

  useEffect(() => {
    const onLogout = (event: CustomEvent<LogoutReasons>) => {
      if (event.detail === LogoutReasons.TokenExpired) {
        toast({
          title:
            "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.",
          variant: "destructive",
        });
      }

      navigate(routePaths.login, { replace: true });
    };

    addCustomEventListener(EVENTS.ACCOUNT_LOGOUT, onLogout);

    return () => {
      removeCustomEventListener(EVENTS.ACCOUNT_LOGOUT, onLogout);
    };
  }, [navigate, toast]);

  return context;
}
